import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    type ReactNode,
} from "react"

import axios from "axios"

import type { AuthContextValue, AuthStatus, User, LoginCredentials } from "@/types/auth.types"
import { USER, AUTH } from "@/config/api.endpoints"
import AppService from "@/services/app.service"

const axiosConfig = {
  baseURL: AppService.getEnv() === 'production' ? AppService.getApiBaseURL() : (import.meta.env.VITE_API_URL || ''),
  timeout: AppService.getApiTimeout() || 10000,
  retryAttempts: 3,
  retryDelay: 1000,
}

const authClient = axios.create({...axiosConfig,
  withCredentials: true,
  headers: {
      "ngrok-skip-browser-warning": "none",
      "Content-Type": "application/json",
      Accept: "application/json",
    },
})

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [authStatus, setAuthStatus] = useState<AuthStatus>("pending")

  const checkAuth = useCallback(async (): Promise<void> => {
    try {
      const { data } = await authClient.get(USER.ME)
      setUser(data)
      setAuthStatus("authenticated")
    } catch {
      setUser(null)
      setAuthStatus("unauthenticated")
    }
  }, [])

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authClient.post(AUTH.LOGOUT)
    } finally {
      setUser(null)
      setAuthStatus("unauthenticated")
    }
  }, [])

  const refreshSession = useCallback(async(): Promise<void> => await authClient.post(AUTH.REFRESH), [])

  const login = useCallback(async (username: string, password: string): Promise<void> => {
    const credentials: LoginCredentials = { username, password }
    try {
      await authClient.post(AUTH.LOGIN, credentials)
      await checkAuth()
    } catch {
      setUser(null)
      setAuthStatus("unauthenticated")
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const value = useMemo(
    () => ({ user, authStatus, login, refreshSession, logout, checkAuth }),
    [user, authStatus, login, refreshSession, logout, checkAuth]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an <AuthProvider>")
  }
  return context
}
